/**
 * Settings — shared by every role.
 *
 * Replaces `buildSettingsHTML` plus its four near-identical wrappers
 * (`renderDoctorSettings`, `renderPatientSettings`, `renderTeamSettings`,
 * `initSettings`) and the four password-change handlers that differed only in
 * which element ids they read.
 *
 * A note on what is genuinely available: the backend exposes
 * PUT /api/auth/profile, which updates only `picture` and `ringtone`. The legacy
 * UI also showed "change username", "change password", "link Gmail" and "verify
 * Gmail" forms — none of which had a server route; `handleChangePassword` wrote
 * to localStorage and reported success. Those forms are not reproduced, because
 * telling a user their password changed when it did not is worse than not
 * offering the option.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Field,
  Icon,
  Input,
  PageHeader,
  Panel,
  SectionTitle,
} from '../../components/common';
import { useAuth, ROLES } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { authApi, doctorApi } from '../../api/endpoints';
import { normalizeDoctor } from '../../domain/catalog';
import { compressImage, readFileAsDataUri, formatBytes, dataUriByteLength } from '../../domain/files';
import { alarmService } from '../../services/alarmService';
import { useData } from '../../context/DataContext';
import { PrescriptionDesigner } from './PrescriptionDesigner';
import { GoogleAccounts } from './GoogleAccounts';

const ROLE_LABELS = {
  [ROLES.ADMIN]: 'مدير النظام',
  [ROLES.DOCTOR]: 'طبيب',
  [ROLES.PATIENT]: 'مستخدم',
  [ROLES.TEAM_MEMBER]: 'عضو فريق التسويق',
};

export function SettingsPage() {
  const { session, role, doctorProfile, setDoctorProfile, updateSession, logout } = useAuth();
  const { success, error: toastError } = useNotifications();

  const [avatar, setAvatar] = useState(null);
  const [ringtone, setRingtone] = useState(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [savingRingtone, setSavingRingtone] = useState(false);

  useEffect(() => {
    setAvatar(doctorProfile?.image || session?.picture || null);
  }, [doctorProfile?.image, session?.picture]);

  const handleAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingAvatar(true);
    try {
      // Avatars are stored as base64 on the user row, so they must be small.
      const dataUri = await compressImage(file, { maxWidth: 400, maxHeight: 400, quality: 0.8 });
      await authApi.updateProfile({ picture: dataUri });

      setAvatar(dataUri);
      updateSession({ picture: dataUri });

      // A doctor's avatar is mirrored onto their DoctorProfile by the backend,
      // so the local copy has to be refreshed to match.
      if (role === ROLES.DOCTOR) {
        const fresh = await doctorApi.profile();
        setDoctorProfile(normalizeDoctor(fresh));
      }

      success('تم تحديث الصورة الشخصية');
    } catch (err) {
      toastError(err.message || 'تعذر تحديث الصورة');
    } finally {
      setSavingAvatar(false);
    }
  };

  const handleRingtone = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSavingRingtone(true);
    try {
      const dataUri = await readFileAsDataUri(file);
      const size = dataUriByteLength(dataUri);

      // The API body limit is 50 MB; a ringtone anywhere near that is a mistake.
      if (size > 3 * 1024 * 1024) {
        throw new Error(`الملف كبير جداً (${formatBytes(size)}). اختر ملفاً أقل من 3 ميجابايت.`);
      }

      await authApi.updateProfile({ ringtone: dataUri });
      setRingtone(dataUri);
      alarmService.setCustomRingtone(dataUri);
      success('تم حفظ نغمة التنبيه');
    } catch (err) {
      toastError(err.message || 'تعذر حفظ النغمة');
    } finally {
      setSavingRingtone(false);
    }
  };

  const clearRingtone = async () => {
    setSavingRingtone(true);
    try {
      await authApi.updateProfile({ ringtone: null });
      setRingtone(null);
      alarmService.setCustomRingtone(null);
      success('تمت العودة للنغمة الافتراضية');
    } catch (err) {
      toastError(err.message || 'تعذر إزالة النغمة');
    } finally {
      setSavingRingtone(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="الإعدادات" subtitle="بيانات حسابك وتفضيلاتك" icon="settings" />

      {/* Profile */}
      <Panel>
        <SectionTitle icon="user">الحساب</SectionTitle>

        <div className="flex flex-wrap items-center gap-5">
          <div className="relative">
            <Avatar src={avatar} name={session?.name} size={80} />
            {savingAvatar && (
              <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/60">
                <Icon name="loader-2" size={22} className="animate-spin text-cyan-400" />
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-lg font-bold text-white">{session?.name}</h3>
            <p className="text-sm text-slate-400" dir="ltr">
              @{session?.username}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="shield">
                {ROLE_LABELS[role] || role}
              </Badge>
              {session?.phone && (
                <Badge className="border-slate-600 bg-slate-800 text-slate-300" icon="phone">
                  {session.phone}
                </Badge>
              )}
            </div>
          </div>

          <label className="btn-ghost cursor-pointer">
            <Icon name="camera" size={16} />
            تغيير الصورة
            <input type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
          </label>
        </div>
      </Panel>

      {/* Doctor-specific */}
      {role === ROLES.DOCTOR && <DoctorProfileSection />}

      {/* Alarm ringtone — patients are the ones with dose alarms */}
      {(role === ROLES.PATIENT || role === ROLES.TEAM_MEMBER) && (
        <Panel>
          <SectionTitle icon="bell-ring">نغمة تنبيه الدواء</SectionTitle>

          <p className="mb-4 text-sm leading-relaxed text-slate-400">
            اختر نغمة مخصصة تُشغَّل عند موعد الجرعة، أو استخدم النغمة الافتراضية.
          </p>

          <div className="flex flex-wrap gap-2">
            <label className="btn-ghost cursor-pointer">
              <Icon name="upload" size={16} />
              رفع نغمة
              <input type="file" accept="audio/*" onChange={handleRingtone} className="hidden" />
            </label>

            <Button variant="subtle" icon="play" onClick={() => alarmService.preview()}>
              تجربة النغمة
            </Button>

            {ringtone && (
              <Button
                variant="subtle"
                icon="rotate-ccw"
                loading={savingRingtone}
                onClick={clearRingtone}
              >
                العودة للافتراضية
              </Button>
            )}
          </div>
        </Panel>
      )}

      {/* Google sign-in addresses — every role, since every role signs in */}
      <GoogleAccounts />

      {/* Session */}
      <Panel>
        <SectionTitle icon="log-out">الجلسة</SectionTitle>
        <p className="mb-4 text-sm leading-relaxed text-slate-400">
          تسجيل الخروج ينهي جلستك على هذا الجهاز فقط.
        </p>
        <Button variant="danger" icon="log-out" onClick={logout}>
          تسجيل الخروج
        </Button>
      </Panel>
    </div>
  );
}

/* ========================================================================== */

/** Prices, title and print branding — the fields PUT /api/doctors/profile accepts. */
function DoctorProfileSection() {
  const { doctorProfile, setDoctorProfile } = useAuth();
  const { categories } = useData();
  const { success, error: toastError } = useNotifications();

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoInput = useRef(null);

  useEffect(() => {
    if (!doctorProfile) return;
    setForm({
      title: doctorProfile.title || '',
      categoryId: doctorProfile.category || '',
      priceExam: doctorProfile.price_exam ?? 500,
      priceFollowup: doctorProfile.price_followup ?? 200,
      logo: doctorProfile.logo || '',
      prescriptionStyle: doctorProfile.prescriptionStyle || 'wave-top',
      printPrimaryColor: doctorProfile.printColors?.primary || '#06b6d4',
      printSecondaryColor: doctorProfile.printColors?.secondary || '#a855f7',
    });
  }, [doctorProfile]);

  if (!form) return null;

  const update = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const handleLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const dataUri = await compressImage(file, { maxWidth: 500, maxHeight: 500, quality: 0.85 });
      setForm((current) => ({ ...current, logo: dataUri }));
    } catch (err) {
      toastError(`تعذر قراءة الشعار: ${err.message}`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await doctorApi.updateProfile({
        ...form,
        priceExam: Number(form.priceExam),
        priceFollowup: Number(form.priceFollowup),
        // Standing discounts are owned by the offers screen; re-send them
        // unchanged so this update does not clear them.
        hasNightDiscount: doctorProfile.hasNightDiscount,
        nightDiscountPercent: doctorProfile.nightDiscountPercent,
        hasFirstVisitDiscount: doctorProfile.hasFirstVisitDiscount,
        firstVisitDiscountPercent: doctorProfile.firstVisitDiscountPercent,
      });

      const fresh = await doctorApi.profile();
      setDoctorProfile(normalizeDoctor(fresh));
      success('تم حفظ بيانات العيادة');
    } catch (err) {
      toastError(err.message || 'تعذر حفظ البيانات');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel>
      <SectionTitle icon="stethoscope">بيانات الطبيب</SectionTitle>

      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="اللقب المهني" hint="مثال: استشاري جراحة عامة">
            <Input value={form.title} onChange={update('title')} />
          </Field>

          <Field label="التخصص">
            <select value={form.categoryId} onChange={update('categoryId')} className="field-input">
              <option value="">اختر التخصص</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="سعر الكشف الافتراضي" hint="يُستخدم عندما لا تحدد العيادة سعراً خاصاً">
            <Input type="number" min="0" value={form.priceExam} onChange={update('priceExam')} />
          </Field>

          <Field label="سعر المتابعة الافتراضي">
            <Input
              type="number"
              min="0"
              value={form.priceFollowup}
              onChange={update('priceFollowup')}
            />
          </Field>
        </div>

        <div className="border-t border-white/10 pt-4">
          <h4 className="mb-3 text-sm font-bold text-cyan-300">هوية الروشتة المطبوعة</h4>

          <div className="flex flex-wrap items-center gap-4">
            {form.logo && (
              <img
                src={form.logo}
                alt="شعار العيادة"
                className="h-16 w-16 rounded-xl border border-white/10 bg-white object-contain p-1"
              />
            )}

            <Button variant="ghost" icon="image" onClick={() => logoInput.current?.click()}>
              {form.logo ? 'تغيير الشعار' : 'رفع شعار'}
            </Button>
            <input
              ref={logoInput}
              type="file"
              accept="image/*"
              onChange={handleLogo}
              className="hidden"
            />

            {form.logo && (
              <Button
                variant="subtle"
                icon="trash-2"
                onClick={() => setForm((c) => ({ ...c, logo: '' }))}
                className="!text-red-300"
              >
                إزالة
              </Button>
            )}
          </div>

          <div className="mt-5 border-t border-white/10 pt-5">
            <PrescriptionDesigner form={form} setForm={setForm} doctorProfile={doctorProfile} />
          </div>
        </div>

        <div className="flex justify-end border-t border-white/10 pt-4">
          <Button icon="save" loading={saving} onClick={save}>
            حفظ البيانات
          </Button>
        </div>
      </div>
    </Panel>
  );
}

export default SettingsPage;
